import { appStoreConstant } from "@/utils/branch"
import clsx from "clsx"
import { Arrow } from "pages/tools"
import { useEffect, useRef } from "react"
import {
  useEditorParamsContext,
  useUploadOption,
} from "sections/editor/editor"
import { z } from "zod"
import Button from "../button"
import { ResponsiveImage } from "../image"
import Tooltip from "../tooltip"
import {
  BGRemover,
  DeformAi,
  GenerativeFill,
  PhotoAi,
  RestyleAi,
} from "./icons"

const featureAiToolSchema = z.enum([
  "deform",
  "ai_photo",
  "ai_restyle",
  "text_to_image",
  "text_to_video",
  "ai_music_generator",
])

export const toolSchema = z.object({
  name: z.string(),
  description: z.string(),
  link: z.string(),
  icon: z.string(),
  media: z.string(),
})

export const toolComponent = z.object({
  component: z.literal("tools"),
  tools: z.array(toolSchema),
  button_text: z.string().optional(),
  button_link: z.string().optional(),
  upload: z.boolean().optional(),
  tool: featureAiToolSchema.optional(),
})

type Tools = z.infer<typeof toolSchema>[]
type Tool = z.infer<typeof toolSchema>
type ToolProps = z.infer<typeof toolComponent>

export function ToolsOld(props: ToolProps) {
  const { tools } = props

  return (
    <section className="pt-[35px] tablet:pt-[40px] desktop:pt-[55px] ">
      <div className="relative flex h-[400px] w-full items-center bg-gradient-to-r from-[#E5F0F599] via-[#E5F0F599] to-[#EA919699] dark:from-[#1e435247] dark:via-[#1e43524f] dark:to-[#b3232a40] tablet:h-[444px] desktop:h-[535px]">
        <div className="pointer-events-none absolute left-0 top-0 z-10 w-full overflow-hidden mix-blend-overlay">
          <ResponsiveImage
            src="/feature/noise"
            className="w-full object-cover dark:opacity-50"
          />
        </div>
        <div className="z-20 flex w-full flex-col items-center gap-8">
          <p className="text-h5">Other helpful tools</p>
          <div className="no-scrollbar flex w-full flex-row gap-3 overflow-x-scroll px-4 tablet:w-[615px] tablet:justify-center tablet:overflow-hidden desktop:w-[930px]">
            {tools.map((tool, index) => (
              <SingleToolOld
                key={index}
                tool={tool}
                image={tool.icon}
              />
            ))}
          </div>
          <Button
            primary
            text="See All Tools"
            href="https://zoomerang.app"
            className="w-[80%] tablet:w-[315px] desktop:mt-14"
          />
        </div>
      </div>
    </section>
  )
}

export default function Tools(props: ToolProps) {
  const { tools, button_text, button_link, upload, tool } =
    props

  const { onClick } = useUploadOption(upload, tool)
  const editor = useEditorParamsContext()

  return (
    <section className="py-[35px] tablet:py-[40px] desktop:py-[55px] ">
      <div className="relative flex w-full items-center">
        {/* <div className="pointer-events-none absolute inset-0 z-10 w-full overflow-hidden mix-blend-overlay">
          <ResponsiveImage
            src="/feature/noise"
            className="h-full w-full object-cover dark:opacity-50"
          />
        </div> */}
        <div className="z-20 flex w-full flex-col items-center gap-8 tablet:py-[20px]">
          <h2 className="text-h5">Other helpful tools</h2>
          <div
            className={clsx(
              "no-scrollbar w-full touch-pan-x overflow-y-hidden overflow-x-scroll px-4",
              tools.length < 5 &&
                "flex desktop:items-center desktop:justify-center",
            )}>
            <div
              className={clsx(
                "flex touch-pan-x gap-2 tablet:gap-3",
              )}>
              <div className="h-10 w-[calc(50vw-500px)] shrink-0" />
              {tools.map((tool, index) => (
                <SingleTool key={index} tool={tool} />
              ))}
              <div className="h-10 w-[calc(50vw-500px)] shrink-0" />
            </div>
          </div>
          {button_text && tool !== "ai_music_generator" && (
            <>
              <Button
                href={button_link}
                onClick={() => {
                  onClick && onClick()
                }}
                loading={editor.loading}
                className="hidden w-[80%] tablet:w-[315px] desktop:block"
                text={button_text}
                primary
              />
              <Button
                href={appStoreConstant}
                text={button_text}
                primary
                full
                className="w-[80%] tablet:!w-[315px] desktop:hidden"
              />
            </>
          )}
          {button_text && tool === "ai_music_generator" && (
            <Button
              loading={editor.loading}
              primary
              onClick={onClick}
              text={button_text}
              className="w-[80%] tablet:!w-[315px]"
            />
          )}
        </div>
      </div>
    </section>
  )
}

export function SingleTool(props: { tool: Tool }) {
  const { name, link, icon, media, description } =
    props.tool

  const player = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (player.current)
      if (player.current.clientHeight >= 208)
        player.current.autoplay = false
      else player.current.play().catch(console.error)
  }, [player])

  return (
    <>
      <a
        href={link}
        className={clsx(
          "flex w-[277px] shrink-0 flex-row items-center justify-between gap-[10px] rounded-[8px] bg-color-surface py-[8px] pl-[8px] pr-[12px]",
          "tablet:w-[233px] tablet:items-start tablet:justify-start tablet:p-0 tablet:pb-4 desktop:w-[200px]",
        )}>
        <div
          className={clsx(
            "flex flex-row items-center gap-[10px]",
            "tablet:w-full tablet:flex-col tablet:gap-[14px]",
          )}>
          <video
            muted
            loop
            autoPlay
            playsInline
            preload="metadata"
            ref={player}
            onPointerEnter={(e) => {
              e.currentTarget.play().catch(console.error)
            }}
            onPointerLeave={(e) => {
              e.currentTarget.pause()
              e.currentTarget.currentTime = 0.001
            }}
            onTouchMove={(e) => {
              e.currentTarget.play().catch(console.error)
            }}
            onTouchEnd={(e) => {
              e.currentTarget.pause()
              e.currentTarget.currentTime = 0.001
            }}
            src={media}
            className={clsx(
              "aspect-1 h-[88px] rounded-[6px] object-cover object-top",
              "transition-all duration-300 tablet:rounded-[8px] tablet:hover:rounded",
              "tablet:aspect-none tablet:h-[208px] tablet:w-full desktop:h-[200px]",
            )}
          />
          <div className="flex flex-col gap-[3px] tablet:w-full tablet:gap-[10px] tablet:px-4">
            <div className="flex flex-col items-start gap-[3px] text-[16px] font-600 text-blue-700 tablet:flex-row tablet:items-center tablet:gap-3 tablet:text-[20px] tablet:text-blue-800 desktop:text-[16px]">
              <div className="h-5 w-5 [--icon-color:var(--color-blue-700)] tablet:[--icon-color:var(--color-blue-800)]">
                {getIconByName(icon)}
              </div>
              <Tooltip
                className="w-[140px]"
                width={180}
                title={name}
                showAlways={name.length > 15}>
                <p className=" w-[140px] truncate leading-[19px] tablet:w-auto tablet:leading-normal">
                  {name}
                </p>
              </Tooltip>
            </div>
            <p className="line-clamp-2 w-full text-[12px] font-400 leading-[15px] text-blue-600 tablet:text-[16px] tablet:leading-normal tablet:text-blue-700">
              {description}
            </p>
          </div>
        </div>
        <div className="[--icon-color:var(--color-placeholder)] tablet:hidden">
          <Arrow />
        </div>
      </a>
    </>
  )
}

function SingleToolOld(props: {
  tool: Tool
  image: string
}) {
  const { image, tool } = props
  const { link, name } = tool

  return (
    <>
      <a
        href={link}
        className="flex h-[178px] w-[145px] shrink-0 flex-col items-center justify-center gap-2 rounded-[30px] bg-color-cell px-5">
        <img
          src={image}
          alt="tool_image"
          className="h-12 w-12 object-cover"
        />
        <p className="text-body-5 text-color-placeholder">
          {name}
        </p>
      </a>
    </>
  )
}

function getIconByName(icon: string) {
  switch (icon) {
    case "deform_ai":
      return <DeformAi />
    case "photo_ai":
      return <PhotoAi />
    case "generative_fill":
      return <GenerativeFill />
    case "bg_remover":
      return <BGRemover />
    case "restyle_ai":
      return <RestyleAi />
  }
}
